export default class Configuration {
  /**
   * Construct a new 'Configuration' object
   * @param {Number} baseWidth The total available width
   * @param {Array} currentConfiguration The content to assign since initialization
   */
  constructor(id, baseWidth, currentConfiguration = []) {
    this.id = id;
    this.baseWidth = baseWidth;
    this.currentConfiguration = currentConfiguration;
  }

  /**
   * Copy deeply the currently instanciated object and return the new instance
   * @param {Number} newId The ID of the cloned configuration
   * @returns {Object} The copied instance
   */
  clone(newId) {
    return new Configuration(newId, this.baseWidth, [...this.currentConfiguration]);
  }

  /**
   * Add a box to the content of the current Configuration object, from a given box width
   * @param {Number} boxWidth The width used by the box that  will be added to the content list
   */
  addBox(boxWidth) {
    this.currentConfiguration.push(boxWidth);
  }

  /**
   * Calculate the delta between the total available width and the used width
   * @returns {Number} The available width
   */
  availableWidth() {
    let leftWidth = this.baseWidth;

    this.currentConfiguration.forEach((obj) => {
      leftWidth -= obj.width;
    });

    return leftWidth;
  }

  /**
   * Return the total width used by the configuration
   * @returns {Number} The used width
   */
  totalWidth() {
    let totalWidth = 0;

    this.currentConfiguration.forEach((obj) => {
      totalWidth += obj.width;
    });

    return totalWidth;
  }

  /**
   * Returns the price of the current content, depending on the measure of the boxes
   * @returns {Number} The total price
   */
  totalPrice() {
    let totalPrice = 0.0;

    this.currentConfiguration.forEach((widthObj) => {
      totalPrice += widthObj.price;
    });

    return totalPrice;
  }

  /**
   * Returns whether the Configuration is over or it can contain more elements.
   * A Configuration is considered as final is its content's available size is lesser than the smallest available box
   * @param {Array} availableWidths The list of the different boxes' widths a Configuation object can put in its content
   * @returns {Boolean} True if the Configuration object cannot contain any more box.
   */
  isFinal(availableWidths) {
    return this.availableWidth() < availableWidths.at(0).width; // Take index 0 because they have been sorted first
  }

  /**
   * Given the state of the 'content' attribute of the current Configuration object, try to construct children Configuration objects,
   * each new child taking one different width, to calculate ALL the solutions.
   * If the Configuration object from which this function is called is already final, no children will be returned (empty Array).
   *
   * Note: the algorithm is very heavy and would need to be lightened!
   * @param {Array} availableWidths The list of the different boxes' widths a Configuation object can put in its content
   * @returns {Array} The array containing all the new Configuration objects
   */
  createChildren(newId, availableWidths) {
    if (this.isFinal(availableWidths)) {
      return [];
    }

    let childrenConfigurations = [];

    // Iterate through the list of the boxes' widths
    for (let i = 0; i < availableWidths.length; i++) {
      // Check if the Configuration has enough space to add a box with the given width
      if (availableWidths.at(i).width <= this.availableWidth()) {
        // Create a new child Configuration and add the new box to its content
        const child = this.clone(newId + i);
        child.addBox(availableWidths.at(i));
        childrenConfigurations.push(child);
      }
    }

    return childrenConfigurations;
  }
}
